import React, { useCallback, useRef } from 'react';
import { Dialog, Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { track } from '../utils/analytics';

/**
 * Full-bleed player for the story film. The <video> only exists while the
 * dialog is open, so the file is never fetched until someone asks for it.
 */
const VideoModal = ({ open, onClose, src, poster }) => {
    const videoRef = useRef(null);

    // Fires once the dialog has finished opening. Playing any earlier races the
    // transition; the film has sound, so a play() the browser doesn't tie to the
    // click gets blocked and the viewer has to press play themselves.
    const startPlayback = useCallback(() => {
        const video = videoRef.current;
        if (!video) return;
        const play = video.play();
        if (play) play.catch(() => { /* blocked anyway; controls are visible */ });
    }, []);

    const handleClose = () => {
        const v = videoRef.current;
        if (v) {
            v.pause();
            const percent = v.duration ? Math.round((v.currentTime / v.duration) * 100) : 0;
            track('film_close', { percent });
        }
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            slotProps={{
                transition: { onEntered: startPlayback },
                paper: {
                    // No DialogTitle, so MUI's auto aria-labelledby points at nothing; name the dialog directly.
                    'aria-label': 'Video',
                    sx: {
                        backgroundColor: '#000',
                        borderRadius: { xs: 0, sm: 2 },
                        overflow: 'hidden',
                        m: { xs: 0, sm: 2 },
                    },
                },
                backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.92)' } },
            }}
        >
            <Box sx={{ position: 'relative', lineHeight: 0 }}>
                <IconButton
                    onClick={handleClose}
                    aria-label="Close video"
                    sx={{
                        position: 'absolute', top: 8, right: 8, zIndex: 2,
                        color: 'rgba(255,255,255,0.85)',
                        backgroundColor: 'rgba(0,0,0,0.4)',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.65)' },
                    }}
                >
                    <CloseIcon />
                </IconButton>

                {open && (
                    <video
                        ref={videoRef}
                        onEnded={() => track('film_complete')}
                        controls
                        autoPlay
                        playsInline
                        preload="auto"
                        poster={poster}
                        style={{ width: '100%', height: 'auto', display: 'block' }}
                    >
                        <source src={src} type="video/mp4" />
                        Your browser does not support the video tag.
                    </video>
                )}
            </Box>
        </Dialog>
    );
};

export default VideoModal;
